import { ACTION_TYPES } from '@/constants';
import store from '../store';
import { EventBus } from './event-bus';
import tokenService, { getTops2Token } from './TokenService';

const CLIENT_FOLLOW_UP = 'Client Follow Up';

let cachedToken = null;
let tokenExpiryTime = null;

const getToken = async () => {
  try {
    // Check if we have a valid cached token
    if (cachedToken && tokenExpiryTime && Date.now() < tokenExpiryTime) {
      return cachedToken;
    }

    // No valid cached token, fetch new one
    console.log('Fetching new token from MSAL');
    const token = await tokenService.getTops2Token();

    if (!token) {
      console.error('Failed to obtain token');
      EventBus.emit('show-notification', {
        type: 'error',
        message: 'Failed to obtain authentication token',
        icon: 'ErrorIcon.80x80',
        persistence: true
      });
      return null;
    }

    // Cache the token with a 55-minute expiry (typical token lifetime is 1 hour)
    cachedToken = token;
    tokenExpiryTime = Date.now() + (55 * 60 * 1000);

    return token;
  } catch (error) {
    console.error('Error getting token:', error);
    throw error; // Propagate the error instead of returning null
  }
}


async function fetchJson(url, token, requestedBy) {
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
      'Requestedby': requestedBy
    },
  });
  if (!res.ok) {
    EventBus.emit('show-notification', {
      type: 'error',
      message: `HTTP error! status: ${res.status}`,
      icon: 'ErrorIcon.80x80',
      persistence: true
    });
    return null;
  }
  return res.json();
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}
async function fetchClientFollowUpEngagements(token, { accessLevel, role, requestedBy, draftDeliveredDateStartDate, draftDeliveredDateEndDate }) {
  const url = `${process.env.VITE_TRACKER_OL_BASE_URL}/tracker/search`;
  let params = new URLSearchParams({
    '_sort': 'updatedTimestamp',
    '_order': 'desc',
    'objectType': 'Assignment',
    'userFilterChildParam.jobOriginator.emailAddress_eq': requestedBy,
    'userFilterChildParam.assignmentStatus.id_eq': 9,
    'userFilterChildParam.draftDeliveredDate_gte': draftDeliveredDateStartDate,
    'userFilterChildParam.draftDeliveredDate_lt': draftDeliveredDateEndDate
  });
  return fetchJson(`${url}?${params.toString()}`, token, requestedBy);
}

async function fetchActiveEngagements(token, { page, pageSize, searchText, accessLevel, role, requestedBy }) {
  const url = `${process.env.VITE_TRACKER_OL_BASE_URL}/tracker/search`;
  let params = new URLSearchParams({
    '_limit': pageSize,
    '_page': page,
    '_sort': 'updatedTimestamp',
    '_order': 'desc',
    'isQuoteAJob': 'true,null',
    'objectType': 'Assignment',
    'stub': 'to-do',
    'tile': 'activeAssignments'
  });
  if (searchText && searchText.trim()) {
    params = new URLSearchParams({
      '_limit': pageSize,
      '_page': page,
      '_sort': 'updatedTimestamp',
      '_order': 'desc',
      'isQuoteAJob': 'true,null',
      'objectType': 'Assignment',
      'stub': 'all',
      'tile': 'activeAssignments',
      'q': searchText.trim()
    });
  }
  return fetchJson(`${url}?${params.toString()}`, token, requestedBy);
}

async function fetchPastEngagements(token, { page, pageSize, searchText, accessLevel, role, requestedBy }) {
  const getDateNDaysAgoUTC = (days) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
  };
  const updatedSince = getDateNDaysAgoUTC(120);
  const url = `${process.env.VITE_TRACKER_OL_BASE_URL}/tracker/search`;
  const params = new URLSearchParams({
    '_limit': pageSize,
    '_page': page,
    '_sort': 'updatedTimestamp',
    '_order': 'desc',
    'objectType': 'Assignment',
    'trackerEngagementStatus.id': '6,8',
    'updatedTimestamp_gte': updatedSince,
    'stub': 'all',
    'tile': 'pastEngagements'
  });
  if (searchText && searchText.trim()) params.append('q', searchText.trim());
  return fetchJson(`${url}?${params.toString()}`, token, requestedBy);
}

const fetchEngagementDetails = async (page = 0, pageSize = 50, searchText) => {
  try {
    const token = await getTops2Token();

    const userDetails = store.getters['email/getUserDetails'];
    console.log('userDetails', userDetails)
    const accessLevel = userDetails.authorizations[0]?.accessLevel?.toLowerCase();
    const role = userDetails.authorizations[0]?.role?.toLowerCase();

    let [activeList, pastList] = await Promise.all([
      fetchActiveEngagements(token, { page, pageSize, searchText, accessLevel, role, requestedBy: userDetails.emailAddress }),
      fetchPastEngagements(token, { page, pageSize, searchText, accessLevel, role, requestedBy: userDetails.emailAddress }),
    ]);

    activeList = normalizeList(activeList);
    pastList = normalizeList(pastList);

    // Add source identifier to distinguish between active and past assignments
    activeList = activeList.map(item => ({ ...item, assignmentSource: 'active' }));

    // Iterate through pastList and set status based on conditions
    pastList = pastList.map(item => {
      // Check for flag conditions to keep as Active
      if (item.flag === true && Array.isArray(item.flagDisplay) &&
          (item.flagDisplay.includes("Need to create invoice") ||
           item.flagDisplay.includes("Invoice not sent to client"))) {
        item.assignmentSource = 'active';
      } else {
        item.assignmentSource = 'past';
      }

      return item;
    });

    let mergedList = [...activeList, ...pastList];

    if(searchText && searchText.trim()) {
      // Create a map to handle duplicates, preferring past assignments when they exist
      const assignmentMap = new Map();

      // First add active assignments
      activeList.forEach(item => {
        if (item.viewEventId) {
          assignmentMap.set(item.viewEventId, item);
        }
      });

      // Then add past assignments (these will override active ones if same viewEventId)
      pastList.forEach(item => {
        if (item.viewEventId) {
          assignmentMap.set(item.viewEventId, item);
        }
      });

      let uniqueList = Array.from(assignmentMap.values());
      return uniqueList;
    } else {
      return mergedList;
    }
  } catch (error) {
    console.error('Error fetching engagement details:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch engagement details. Please try again.'
    });
    return [];
  }
}

/**
 * Lightweight: fetch only the raw eligible assignment IDs for Client Follow-Up.
 * Calls the tracker search with draftDeliveredDate filter (single API call).
 * Does NOT cross-check recent actions — fast enough for the warning banner check.
 */
const fetchClientFollowUpEligibleIdsOnly = async ({ dateStart, dateEnd } = {}) => {
  const token = await getTops2Token();
  const userDetails = store.getters['email/getUserDetails'];
  if (!userDetails?.emailAddress) throw new Error('User email address is required');

  const formatDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : null;
  const startDate = formatDate(dateStart) || formatDate(new Date(Date.now() - 20 * 24 * 60 * 60 * 1000));
  const endDate   = formatDate(dateEnd)   || formatDate(new Date());

  const data = await fetchClientFollowUpEngagements(token, {
    accessLevel: userDetails.authorizations?.[0]?.accessLevel?.toLowerCase(),
    role:        userDetails.authorizations?.[0]?.role?.toLowerCase(),
    requestedBy: userDetails.emailAddress,
    draftDeliveredDateStartDate: startDate,
    draftDeliveredDateEndDate:   endDate,
  });

  const list = normalizeList(data);
  return [...new Set(
    list.map(item => String(item.viewEventId || item.fileId || item.id || '')).filter(Boolean)
  )];
};

/**
 * Lightweight: fetch only the raw eligible assignment IDs for Property Follow-Up.
 * Does NOT enrich via tracker — used for the eligibility check in the warning banner.
 */
const fetchPropertyFollowUpEligibleIdsOnly = async (userEmailAddress) => {
  const token = await getTops2Token();

  if (!userEmailAddress) {
    const userDetails = store.getters['email/getUserDetails'];
    userEmailAddress = userDetails?.emailAddress;
  }
  if (!userEmailAddress) throw new Error('User email address is required');

  const url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/property-follow-up-eligible-assignments`;
  const params = new URLSearchParams({ userEmailAddress, page: 0, pageSize: 9999 });
  const response = await fetch(`${url}?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${await response.text().catch(() => '')}`);
  }

  const data = await response.json();
  if (!Array.isArray(data)) return [];

  return [...new Set(
    data.map(item => {
      if (typeof item === 'string' || typeof item === 'number') return String(item);
      return String(item.fileId || item.id || '');
    }).filter(Boolean)
  )];
};

/**
 * Fetch assignments eligible for Property Follow-Up
 * These are assignments where Property Contact Request was sent but NOT Property Contact Follow Up
 */
const fetchPropertyFollowUpEligibleAssignments = async (userEmailAddress, page = 0, pageSize = 5) => {
  try {
    const token = await getTops2Token();
    
    if (!userEmailAddress) {
      const userDetails = store.getters['email/getUserDetails'];
      userEmailAddress = userDetails?.emailAddress;
    }

    if (!userEmailAddress) {
      throw new Error('User email address is required');
    }

    const url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/property-follow-up-eligible-assignments`;
    const params = new URLSearchParams({
      userEmailAddress: userEmailAddress,
      page: page,
      pageSize: pageSize
    });
    
    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    
    // Normalize the response - expect array of IDs or objects with id/fileId
    let allAssignmentIds = [];
    if (Array.isArray(data)) {
      // Handle array of IDs or objects
      const rawIds = data.map(item => {
        if (typeof item === 'string' || typeof item === 'number') {
          return item;
        } else if (item.id) {
          return item.id;
        } else if (item.fileId) {
          return item.fileId;
        }
        return null;
      }).filter(id => id !== null);
      
      // Deduplicate IDs immediately
      allAssignmentIds = [...new Set(rawIds)];
    }

    if (allAssignmentIds.length === 0) {
      return { assignments: [], hasMore: false };
    }

    // Apply client-side pagination since backend doesn't support it yet
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const assignmentIds = allAssignmentIds.slice(startIndex, endIndex);
    // Determine if there are more items purely based on ID list length
    const hasMore = endIndex < allAssignmentIds.length;
    
    if (assignmentIds.length === 0) {
      return { assignments: [], hasMore: hasMore };
    }
    // Get user details for tracker search
    const userDetails = store.getters['email/getUserDetails'];
    const accessLevel = userDetails.authorizations[0]?.accessLevel?.toLowerCase();
    const role = userDetails.authorizations[0]?.role?.toLowerCase();

    const trackerUrl = `${process.env.VITE_TRACKER_OL_BASE_URL}/tracker/search`;
    
    // Fetch assignments individually
    const fullAssignments = [];
    
    for (const assignmentId of assignmentIds) {
      try {
        // First, try to find in active assignments
        const trackerParams = new URLSearchParams({
          '_limit': 5,
          '_page': 1,
          '_sort': 'updatedTimestamp',
          '_order': 'desc',
          'isQuoteAJob': 'true,null',
          'objectType': 'Assignment',
          'stub': 'all',
          'tile': 'activeAssignments',
          'q': assignmentId.toString().trim()
        });

        const trackerResponse = await fetch(`${trackerUrl}?${trackerParams.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Azuretoken': token,
            'Requestedby': userDetails.emailAddress
          },
        });

        let matchedAssignment = null;

        if (trackerResponse.ok) {
          const trackerData = await trackerResponse.json();
          const assignments = normalizeList(trackerData);
          
          // Find exact match by fileId (which is the assignment ID like CB25US042177-1)
          matchedAssignment = assignments.find(a => 
            a.fileId === assignmentId || 
            a.id === assignmentId || 
            a.viewEventId === assignmentId
          );
          
          if (!matchedAssignment && assignments.length > 0) {
            // If no exact match, take the first result
            matchedAssignment = assignments[0];
          }
        }

        // If not found in active assignments, search in past engagements
        if (!matchedAssignment) {
          const getDateNDaysAgoUTC = (days) => {
            const d = new Date();
            d.setUTCDate(d.getUTCDate() - days);
            return d.toISOString().slice(0, 10);
          };
          const updatedSince = getDateNDaysAgoUTC(120);
          
          const pastParams = new URLSearchParams({
            '_limit': 5,
            '_page': 1,
            '_sort': 'updatedTimestamp',
            '_order': 'desc',
            'objectType': 'Assignment',
            'trackerEngagementStatus.id': '6,8',
            'updatedTimestamp_gte': updatedSince,
            'dashboard': 'all',
            'role': role,
            'accessLevel': accessLevel,
            'tile': 'pastEngagements',
            'q': assignmentId.toString().trim()
          });

          const pastResponse = await fetch(`${trackerUrl}?${pastParams.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Azuretoken': token,
              'Requestedby': userDetails.emailAddress
            },
          });

          if (pastResponse.ok) {
            const pastData = await pastResponse.json();
            const pastAssignments = normalizeList(pastData);
            
            // Find exact match in past engagements
            matchedAssignment = pastAssignments.find(a => 
              a.fileId === assignmentId || 
              a.id === assignmentId || 
              a.viewEventId === assignmentId
            );
            
            if (!matchedAssignment && pastAssignments.length > 0) {
              matchedAssignment = pastAssignments[0];
            }

            if (matchedAssignment) {
              // Mark this assignment as coming from past engagements
              matchedAssignment._isPastEngagement = true;
            }
          }
        }

        if (matchedAssignment) {
          fullAssignments.push(matchedAssignment);
        } 
      } catch (error) {
        console.error(`❌ Error fetching assignment ${assignmentId}:`, error);
        // Continue with next assignment
      }
    }
    
    // Deduplicate assignments by fileId/id before returning
    const uniqueAssignments = [];
    const seenIds = new Set();
    
    for (const a of fullAssignments) {
      const id = a.fileId || a.id;
      if (!seenIds.has(id)) {
        seenIds.add(id);
        uniqueAssignments.push(a);
      }
    }
    

    // Map to standard assignment format
    const mappedAssignments = uniqueAssignments.map(item => ({
      id: item.id,
      code: item?.fileId,
      trackerEngagementStatus: item.trackerEngagementStatus,
      assignmentSource: item._isPastEngagement ? 'past' : 'active',
      propertyName: item.propertyAddress?.propertyName,
      propertyType: item.propertyType?.name,
      primaryAppraiser: item.primaryValuer?.fullName,
      originator: item.jobOriginator?.fullName,
      client: item.client?.clientName,
      clientDetails: item.client,
      propertyAddress: item.propertyAddress,
      address: `${item.propertyAddress?.propertyName || ''} ${item.propertyAddress?.city || ''}`.trim(),
      workFileLink: item.workFileLink,
      viewEventId: item.viewEventId,
      draftDeliveredDate: item.draftDeliveredDate,
      sourceSystemId: item.sourceSystemId,
      clientReferenceNumber: item.clientReferenceNumber,
      objectType: item.objectType,
      deadline: {
        assignmentDueDate: item.deadline?.assignmentDueDate || item.assignmentDueDate || item.dueDate
      },
      primaryValuer: item.primaryValuer,
      primaryValuerName: item.primaryValuer?.fullName,
      primaryValuerEmail: item.primaryValuer?.emailAddress,
      originatorEmail: item.jobOriginator?.emailAddress,
      valuers: item.valuers || [],
      createdTimestamp: item.createdTimestamp,
      userFriendlyId: item.engagementId,
      subjectProperty: item.subjectProperty
    }));
    
    return {
      assignments: mappedAssignments,
      hasMore: hasMore
    };
  } catch (error) {
    console.error('❌ Error fetching property follow-up eligible assignments:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch eligible assignments. Please try again.'
    });
    return [];
  }
}

const fetchClientFollowUpRecentActionIds = async (assignmentIds) => {
  const token = await getTops2Token();
  const recentActions = await fetchRecentActionsByAssignmentIds(assignmentIds, CLIENT_FOLLOW_UP, token);
  const extractedAssignmentIds = extractAssignmentIdsFromRecentActions(recentActions);
  console.log('RecentActions by assignmentIds size', recentActions?.length || 0);
  return extractedAssignmentIds;
};

const fetchEngagementDetailsClientFollowUp = async (draftDeliveredDateStartDate, draftDeliveredDateEndDate) => {
  try {
    const token = await getTops2Token();
    const userDetails = store.getters['email/getUserDetails'];
    // Add null check for userDetails
    if (!userDetails) {
      console.error('User details not available');
      throw new Error('User details are required to fetch client follow-up engagements');
    }

    const accessLevel = userDetails.authorizations?.[0]?.accessLevel?.toLowerCase();
    const role = userDetails.authorizations?.[0]?.role?.toLowerCase();

    let engagementList = await fetchClientFollowUpEngagements(token, {accessLevel, role, requestedBy: userDetails.emailAddress, draftDeliveredDateStartDate, draftDeliveredDateEndDate });

    // Add null check for engagementList
    if (!engagementList || !Array.isArray(engagementList)) {
      console.warn('No engagements found or invalid response');
      return [];
    }

    const assignmentIds = engagementList.map(engagement => engagement.viewEventId);
    console.log('Get recent actions by assignmentIds', assignmentIds);

    // Add null check before fetching recent actions
    if (!assignmentIds || assignmentIds.length === 0) {
      console.log('No assignment IDs to fetch recent actions for');
      return normalizeList(engagementList);
    }

    const extractedAssignmentIds = await fetchClientFollowUpRecentActionIds(assignmentIds);

    // Filter engagementList to exclude engagements that have recent actions
    if (extractedAssignmentIds && extractedAssignmentIds.length > 0) {
      engagementList = engagementList.filter(engagement =>
        !extractedAssignmentIds.includes(engagement.id)
      );
    }

    return normalizeList(engagementList);
  } catch (error) {
    console.error('Error fetching engagement details:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch engagement details. Please try again.'
    });
    return [];
  }
}

const fetchUserDetails = async (emailAddress) => {
  try {
    const token = await getTops2Token();
    const encodedEmail = encodeURIComponent(emailAddress);
    const url = `${process.env.VITE_COMMON_OL_BASE_URL}/user-office-common/user/email/${encodedEmail}?source=TrackerOl`;
    return fetchJson(url, token);
  } catch (error) {
    console.error('Error fetching user details:', error);
     EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch user details. Please try again.'
    });
  }
}

// Fetch user role details from the endpoint with authentication
const fetchUserRoleDetails = async (viewUserRoleId) => {
  try {
    const token = await getTops2Token();
    const apiUrl = `${process.env.VITE_COMMON_OL_BASE_URL}/user-office-common/users/viewUserRoleDetails/${viewUserRoleId}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const roleData = await response.json();
    return roleData;

  } catch (error) {
    console.error('Error fetching user role details:', error);
    throw error;
  }
}

const fetchLookups = async () => {
  try {
    const token = await getTops2Token();
    const url = `${process.env.VITE_COMMON_OL_BASE_URL}/reference-common/engagements/lookups`;

    const response = await fetch(url, {
      method: 'GET',
      culture: 'us',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
        'env': process.env.VITE_DYNAMIC_ENV_HEADER,
      },
    });

    if (!response.ok) {
      EventBus.emit('show-notification', {
        type: 'error',
        message: `HTTP error! status: ${response.status}`,
        icon: 'ErrorIcon.80x80',
        persistence: true
      });
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching lookups:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch lookups. Please try again.'
    });
    throw error; // Propagate the error for further handling if needed
  }
}

const fetchAllLookups = async () => {
  try {
    const token = await getTops2Token();
    const url = `${process.env.VITE_COMMON_OL_BASE_URL}/lookup-common/all`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
        'env': process.env.VITE_DYNAMIC_ENV_HEADER,
      },
    });

    if (!response.ok) {
      EventBus.emit('show-notification', {
        type: 'error',
        message: `HTTP error! status: ${response.status}`,
        icon: 'ErrorIcon.80x80',
        persistence: true
      });
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching all lookups:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch all lookups. Please try again.'
    });
    throw error; // Propagate the error for further handling if needed
  }
}

const uploadFileToFolder = async (folderId, multiPartFormData, config) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_COMMON_OL_BASE_URL}/box/uploadFile/${folderId}`;

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
      'env': process.env.VITE_DYNAMIC_ENV_HEADER,
    },
    body: multiPartFormData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    EventBus.emit('show-notification', {
      type: 'error',
      message: `File upload failed: HTTP ${response.status}: ${errorText}`,
      icon: 'ErrorIcon.80x80',
      persistence: true
    });
    return null;
  }

  return await response.json();
}

const changeAssignmentDueDate = async (payload) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/viewSync/updateDueDate`;

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Azuretoken': token,
      'env': process.env.VITE_DYNAMIC_ENV_HEADER,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    EventBus.emit('show-notification', {
      type: 'error',
      message: `HTTP error! status: ${response.status}: ${errorText}`,
    });

    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  await handleRecentActions(payload, ACTION_TYPES.CHANGE_DUE_DATE);
  return data;
}

const fetchRecentActionsByAssignmentIds = async (assignmentIds, actionType, token) => {
  let url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/recent-actions/by-assignments`;
  
  // Add actionType as query parameter if provided
  if (actionType) {
    url += `?actionType=${encodeURIComponent(actionType)}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    body: JSON.stringify(assignmentIds),
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

const extractAssignmentIdsFromRecentActions = (recentActions, actionType) => {
  if (!Array.isArray(recentActions)) {
    console.warn('extractAssignmentIdsFromRecentActions: Expected array, got:', typeof recentActions);
    return [];
  }

  const allAssignmentIds = recentActions
    .filter(action => action && Array.isArray(action.assignmentIds) && (!actionType || action.action === actionType))
    .flatMap(action => action.assignmentIds)
    .filter(id => id != null && id !== '');

  // Return unique assignment IDs
  return [...new Set(allAssignmentIds)];
}

const fetchRecentActions = async (userEmail, pageNumber = 0, pageSize = 10) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/recent-actions`;
  const params = new URLSearchParams({
    'userEmailAddress': userEmail,
    'pageNumber': pageNumber,
    'pageSize': pageSize
  });

  const fullUrl = `${url}?${params.toString()}`;
  console.log('Fetching recent actions from:', fullUrl);

  const response = await fetch(fullUrl, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

const handleRecentActions = async (payload, action) => {
    try {
    const recentActionPayload = generateRecentActionPayload(payload, action);
    await saveRecentActions(recentActionPayload);
    EventBus.emit('refreshRecentActions');
    } catch (error) {
        console.error('Error saving recent actions:', error);
    }

}

const saveRecentActions = async (payload) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/recent-actions`;

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    EventBus.emit('show-notification', {
      type: 'error',
      message: `Failed to save recent action: HTTP ${response.status}: ${errorText}`
    });
    console.error('Error saving recent action:', errorText);
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

const fetchDataRequest = async (userFriendlyId) => {
  try {
    const token = await getTops2Token();
    const url = `${process.env.VITE_QUOTE_OL_BASE_URL}/engagements/dataRequest/${userFriendlyId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
        'env': process.env.VITE_DYNAMIC_ENV_HEADER,
      },
    });

    if (!response.ok) {
      EventBus.emit('show-notification', {
        type: 'error',
        message: `HTTP error! status: ${response.status}`,
        icon: 'ErrorIcon.80x80',
        persistence: true
      });
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching data request type:', error);
    EventBus.emit('show-notification', {
      type: 'error',
      message: 'Failed to fetch data request type. Please try again.'
    });
    throw error; // Propagate the error for further handling if needed
  }
}

const generateRecentActionPayload = (payload, action) => {
    const userEmail = store.getters['email/getLoggedInUserEmailAddress'];
    const selectedEmail = store.getters['email/currentEmail'];

    // Handle null/undefined selectedEmail with safe defaults
    const ccEmailList = selectedEmail?.cc?.map(email => email.emailAddress) || [];
    const toEmailList = [
        ...(selectedEmail?.to?.map(email => email.emailAddress) || []),
        ...(payload.toAddress ? [payload.toAddress] : [])
    ];
    const userDetails = store.getters['email/getUserDetails'];
    return {
        userEmailAddress: userEmail ? userEmail : userDetails.emailAddress,
        action: action,
        title: action,
        description: recentActionDescriptions(action, payload),
        assignmentIds: payload.entityIds,
        emailMessageId: selectedEmail?.internetMessageId || 'No email selected',
        fromAddress: selectedEmail?.from?.emailAddress || userDetails.emailAddress,
        toAddresses: toEmailList,
        ccAddresses: ccEmailList,
        subjectLine: payload.subjectLine ? payload.subjectLine : selectedEmail?.subject || null,
        fileId: payload.fileId,
        emailReceivedDate: selectedEmail?.dateTimeCreated || new Date().toISOString(),
        engagementId: payload.engagementId,
        engagementStatus: payload.engagementStatus,
        engagementNumber: payload.engagementNumber,
        folderId: payload.folderId,
        propertyContact: payload.propertyContact
    };
}

const recentActionDescriptions = (action, payload) => {
    const actions = {
        'Route To Admin':'Admin Request Created',
        'Create New Quote':'Email Sent to TOPS2',
        'Save to Box':'Email saved to Box',
        'Change Due Date': 'Due date changed to replaceValue',
        'Upload POD': 'POD uploaded on replaceValue',
        'Quote Won':'Quote marked as WON',
        'Quote Lost':'Quote marked as LOST',
        'Forward to Appraiser':'Email forwarded to Appraisers',
        'Property Contact Request':'Property Contact Request Template Created',
        'Client Introduction':'Client Introduction Template Created',
        'Property Contact Follow Up': 'Property Contact Follow Up Email Sent',
        'CLIENT_FOLLOW_UP': 'Client Follow Up Email Sent',
        'Client Follow Up': 'Client Follow Up Email Sent',
        'UPLOAD_POD': 'POD uploaded on replaceValue',
        'CHANGE_DUE_DATE': 'Due date changed to replaceValue',
        'SAVE_TO_BOX':'Email saved to Box'
    }
    let replaceValue = '';
    if (!actions[action]) {
        return 'Unknown action';
    }
    if (action == ACTION_TYPES.CHANGE_DUE_DATE) {
      const formatter = new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          timeZone: 'UTC'
        });
        replaceValue = formatter.format(new Date(payload.assignmentDueDate));
    } else if (action == ACTION_TYPES.UPLOAD_POD) {
        const currentDate = new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
        replaceValue = currentDate;
    }
    return actions[action].replace('replaceValue', replaceValue) || 'Unknown action';
}


const formatDate = (dateString) => {
    const [monthStr, dayStr, yearStr] = dateString.split('/');
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const getOrdinalSuffix = (n) => {
        if (n >= 11 && n <= 13) return 'th';
        switch (n % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    return `${monthNames[month - 1]} ${day}${getOrdinalSuffix(day)}, ${year}`;
}

const addProofOfDelivery = async (payload) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/proofOfDelivery/add/0`;

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Azuretoken': token,
      'env': process.env.VITE_DYNAMIC_ENV_HEADER,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return await response.json();
}

const updateDeliveryDate = async (payload) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/viewSync/updateDeliveryDate`;

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Azuretoken': token,
      'env': process.env.VITE_DYNAMIC_ENV_HEADER,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  await handleRecentActions(payload, ACTION_TYPES.UPLOAD_POD);
  return data;
}

const getAssignmentChecklist = async (eventId) => {
  const token = await getTops2Token();
  const fullUrl = `${process.env.VITE_DDR_OL_BASE_URL}/checklist/${eventId}`;

  const response = await fetch(fullUrl, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return await response.json();
}

const getBoxToken = async (folderId) => {
  try {
    const token = await getTops2Token();
    const url = `${process.env.VITE_COMMON_OL_BASE_URL}/box/token/user/${folderId}`;

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      culture: 'us',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
        'env': process.env.VITE_DYNAMIC_ENV_HEADER,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching lookups:', error);
    throw error;
  }
}

const addMetrics = async (payload) => {
  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/outlook-addin/metrics`;
  const userDetails = store.getters['email/getUserDetails'];
  const selectedEmail = store.getters['email/currentEmail']
  const ccEmailList = selectedEmail.cc.map(email => email.emailAddress);
  const toEmailList = [
    ...(selectedEmail?.to?.map(email => email?.emailAddress).filter(Boolean) || []),
    ...(payload.toAddress ? [payload.toAddress] : [])
  ];

  payload.fromEmailAddress = selectedEmail?.from?.emailAddress || userDetails.emailAddress;
  payload.toEmailAddresses = toEmailList;
  payload.ccEmailAddresses = ccEmailList;
  payload.emailObjectId = selectedEmail?.internetMessageId || null;
  payload.emailSubject = payload.subjectLine ? payload.subjectLine : selectedEmail?.subject || null;
  payload.emailReceivedDate = selectedEmail?.dateTimeCreated || null;
  payload.actionDate = new Date().toISOString();

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Error saving metrics:', errorText);
  }

  return await response.json();
}

// Cache for assignment overview data to avoid redundant API calls
const assignmentOverviewCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Utility function to clear cache (useful for development/debugging)
const clearAssignmentOverviewCache = () => {
  assignmentOverviewCache.clear();
  console.log('Assignment overview cache cleared');
};

// Utility function to clear cache for a specific assignment
const clearAssignmentCache = (assignmentId) => {
  if (assignmentId) {
    assignmentOverviewCache.delete(assignmentId);
    console.log(`Assignment overview cache cleared for assignment: ${assignmentId}`);
  }
};

const fetchAssignmentOverviewData = async (assignmentId) => {
  // Quick validation
  if (!assignmentId) {
    throw new Error('Assignment ID is required');
  }

  // Check cache first for instant return
  const cacheKey = assignmentId;
  const cached = assignmentOverviewCache.get(cacheKey);

  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.data;
  }

  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/assignmentOverview/${assignmentId}`;

  // Use the exact same headers as the original implementation to avoid CORS issues
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();

  if (data) {
    // Cache the result with immediate storage
    assignmentOverviewCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  return data;
};

const saveAssignmentOverviewData = async (assignmentOverviewPayload, propertyContactData) => {
  // Quick validation
  if (!assignmentOverviewPayload || !assignmentOverviewPayload.viewEventId) {
    throw new Error('Assignment overview data with viewEventId is required');
  }

  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/assignmentOverview/save`;

  // Merge the property contact data into the existing assignment overview structure
  const updatedPayload = {
    ...assignmentOverviewPayload,
    valuationAssignmentInformation: {
      ...assignmentOverviewPayload.valuationAssignmentInformation,
      propertyContacts: {
        ...assignmentOverviewPayload.valuationAssignmentInformation?.propertyContacts,
        propertyContactName: propertyContactData.propertyContactName,
        propertyContactEmail: propertyContactData.propertyContactEmail
      }
    }
  };

  // Handle proposed inspection date - either update with new value or clear existing one
  if (propertyContactData.hasOwnProperty('proposedInspectionDateTime')) {
    updatedPayload.deadlines = {
      ...assignmentOverviewPayload.deadlines,
      inspectedDate: propertyContactData.proposedInspectionDateTime
    };
  }

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    },
    body: JSON.stringify(updatedPayload)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  // Handle response parsing with better error handling
  let data = null;
  const responseText = await response.text();

  if (responseText.trim() === '') {
    // Empty response is valid for successful saves
    console.log('Received empty response - save operation completed successfully');
    data = { success: true };
  } else {
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('Failed to parse JSON response:', responseText);
      throw new Error(`Invalid JSON response from server: ${parseError.message}. Response: ${responseText.substring(0, 200)}`);
    }
  }

  // Clear the cache for this assignment to ensure fresh data on next fetch
  const cacheKey = assignmentOverviewPayload.viewEventId;
  assignmentOverviewCache.delete(cacheKey);

  return data;
};

const fetchAllocationData = async (viewEventId) => {
  if (!viewEventId) {
    throw new Error('View Event ID is required');
  }

  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/viewSync/allocations/${viewEventId}`;

  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token
    }
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

  // Method to make POST request to Common OL API
async function postToCommonOL(endpoint, payload) {
    try {
      // Get authentication token
      const token = await getTops2Token();


      if (!token) {
        throw new Error('Authentication token not available');
      }

      const url = `${process.env.VITE_COMMON_OL_BASE_URL}${endpoint}`;

      const requestOptions = {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Azuretoken': token,
        },
        body: JSON.stringify(payload)
      };

      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Common OL API request failed:', error);
      throw error;
    }
  
  };

export {
    fetchEngagementDetails,
    fetchPropertyFollowUpEligibleAssignments,
    fetchPropertyFollowUpEligibleIdsOnly,
    fetchClientFollowUpEligibleIdsOnly,
    fetchEngagementDetailsClientFollowUp,
    fetchClientFollowUpRecentActionIds,
    uploadFileToFolder,
    changeAssignmentDueDate,
    fetchRecentActions,
    saveRecentActions,
    getToken,
    addProofOfDelivery,
    updateDeliveryDate,
    getAssignmentChecklist,
    fetchLookups,
    fetchAllLookups,
    getBoxToken,
    fetchUserDetails,
    fetchUserRoleDetails,
    handleRecentActions,
    addMetrics,
    fetchAssignmentOverviewData,
    saveAssignmentOverviewData,
    clearAssignmentOverviewCache,
    clearAssignmentCache,
    postToCommonOL,
    fetchAllocationData,
    fetchAllocations,
    fetchDataRequest,
    fetchEngagementOverviewDocument
};

const fetchAllocations = async (viewEventId) => {
  if (!viewEventId) {
    throw new Error('ViewEventId is required to fetch allocations');
  }

  const token = await getTops2Token();
  const url = `${process.env.VITE_DDR_OL_BASE_URL}/viewSync/allocations/${viewEventId}`;

  try {
    console.log('🔍 Fetching allocations from:', url);
    const response = await fetchJson(url, token, 'SendAdminRequest.getAllocations');

    console.log('📊 Raw allocations response:', response);

    // Look for appraiserAllocationBillingModel in the response
    if (response && response.appraiserAllocationBillingModel) {
      return response.appraiserAllocationBillingModel;
    }

    console.warn('⚠️ appraiserAllocationBillingModel not found in response');
    return [];
  } catch (error) {
    console.error('❌ Error fetching allocations:', error);
    throw error;
  }
};

// Fetch engagement overview document data by engagementId
// This uses the engagement-overview-ol service. The caller should supply a valid engagementId.
// Response shape is passed through; errors propagate for higher-level handling.
async function fetchEngagementOverviewDocument(engagementId, workfileId) {
  if (!engagementId) {
    throw new Error('engagementId is required to fetch engagement overview document');
  }
  const token = await getTops2Token();
  const baseUrl = process.env.VITE_EO_BASE_URL;
  const workfileIdParam = workfileId ? `?workfileId=${encodeURIComponent(workfileId)}` : '';
  const url = `${baseUrl}/document/engagement/${encodeURIComponent(engagementId)}/signedEngagementLetter${workfileIdParam}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Azuretoken': token,
    }
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
}

